// NOVA-POSHTA-117A-R6 — settlement autocomplete (replaceOnFirstInput + reliable search render).
import { useEffect, useRef, useState, type KeyboardEvent, type RefObject } from "react";
import type { StoreV2Lang } from "./store-v2-locale.js";
import {
  searchNovaPoshtaSettlements,
  type SettlementOption,
} from "./nova-poshta-reference-client.js";
import {
  applyReplaceOnFirstInput,
  emitDeliveryAnalytics,
  handleFirstKeyReplaceSelection,
  NP_CITY_DEBOUNCE_MS,
  NP_CITY_MIN_QUERY,
  NP_COMBOBOX_DEBOUNCE_MS,
  npCityHintCopy,
  npDirectoryErrorCopy,
} from "./nova-poshta-combobox-replace.js";

export type NovaPoshtaSettlementValue = {
  ref: string;
  cityRef: string;
  label: string;
  region: string | null;
  area: string | null;
  settlementType: string | null;
};

function highlightParts(label: string, query: string): { before: string; match: string; after: string } | null {
  const q = query.trim();
  if (q.length < NP_CITY_MIN_QUERY) return null;
  const idx = label.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return null;
  return {
    before: label.slice(0, idx),
    match: label.slice(idx, idx + q.length),
    after: label.slice(idx + q.length),
  };
}

function comboboxFocusSelectAll(inputRef: RefObject<HTMLInputElement | null>): void {
  requestAnimationFrame(() => {
    const el = inputRef.current;
    if (!el) return;
    try {
      el.select();
    } catch {
      /* mobile WebView may ignore */
    }
  });
}

export function NovaPoshtaSettlementCombobox({
  lang,
  disabled,
  value,
  onChange,
  onProviderError,
}: {
  lang: StoreV2Lang;
  disabled?: boolean;
  value: NovaPoshtaSettlementValue | null;
  onChange: (next: NovaPoshtaSettlementValue | null) => void;
  onProviderError: (message: string | null, meta?: { rateLimited?: boolean; retryAfterSec?: number | null }) => void;
}) {
  const locale = lang === "en" ? "en" : "uk";
  const copyLang = lang === "en" ? "en" : "uk";
  const [query, setQuery] = useState(value?.label ?? "");
  const [items, setItems] = useState<SettlementOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const replaceArmedRef = useRef(false);
  const searchStartedRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const requestGenRef = useRef(0);
  const focusedRef = useRef(false);

  useEffect(() => {
    if (value?.ref) {
      setQuery(value.label);
      replaceArmedRef.current = false;
      setItems([]);
    }
  }, [value?.ref, value?.label]);

  useEffect(() => {
    if (disabled) {
      setItems([]);
      setLoading(false);
      return;
    }
    const trimmed = query.trim();
    if (trimmed.length < NP_CITY_MIN_QUERY) {
      setItems([]);
      setLoading(false);
      // CUSTOMER-R4.15-C — short/empty city query is neutral, not a provider failure.
      onProviderError(null);
      return;
    }
    // Selected label showing — do not search until user edits.
    if (value?.ref && query === value.label) {
      setItems([]);
      setLoading(false);
      return;
    }

    const timer = window.setTimeout(() => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      const gen = ++requestGenRef.current;
      if (!searchStartedRef.current) {
        searchStartedRef.current = true;
        emitDeliveryAnalytics("delivery.city_search_started");
      }
      setLoading(true);
      void searchNovaPoshtaSettlements(trimmed, locale, ac.signal).then((res) => {
        if (ac.signal.aborted || gen !== requestGenRef.current) return;
        setLoading(false);
        if (!res.ok) {
          emitDeliveryAnalytics("delivery.directory_error", { deliveryType: "city" });
          if (res.rateLimited) {
            onProviderError(npDirectoryErrorCopy(copyLang), {
              rateLimited: true,
              retryAfterSec: res.retryAfterSec ?? 5,
            });
          } else {
            onProviderError(npDirectoryErrorCopy(copyLang));
          }
          setItems([]);
          return;
        }
        onProviderError(null);
        setItems(res.items);
        setActiveIndex(-1);
      });
    }, NP_CITY_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      // Do not abort here — only abort when a newer search starts (above).
      // Aborting on every dep cleanup cancelled in-flight results after city clear (R5).
    };
  }, [query, locale, disabled, value?.ref, value?.label, onProviderError, copyLang]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const clearSelection = () => {
    searchStartedRef.current = false;
    requestGenRef.current += 1;
    abortRef.current?.abort();
    onProviderError(null);
    onChange(null);
  };

  const applyEdit = (nextRaw: string, insertedData?: string | null) => {
    const result = applyReplaceOnFirstInput({
      replaceArmed: replaceArmedRef.current,
      selectedLabel: value?.label ?? null,
      currentQuery: query,
      nextValue: nextRaw.slice(0, 120),
      ...(insertedData !== undefined ? { insertedData } : {}),
    });
    replaceArmedRef.current = false;
    setQuery(result.nextQuery);
    if (result.shouldClearSelection && value) clearSelection();
  };

  const pick = (s: SettlementOption) => {
    if (!s.settlementRef || s.settlementRef.length < 8) return;
    // CUSTOMER-R4.15-C — abort in-flight city search so a late failure cannot
    // paint a red "NP down" banner after the city is already selected.
    requestGenRef.current += 1;
    abortRef.current?.abort();
    onProviderError(null);
    setLoading(false);
    onChange({
      ref: s.settlementRef,
      cityRef: s.cityRef ?? s.settlementRef,
      label: s.label,
      region: s.region,
      area: s.area,
      settlementType: s.settlementType,
    });
    setQuery(s.label);
    setItems([]);
    setActiveIndex(-1);
    replaceArmedRef.current = false;
    searchStartedRef.current = false;
    emitDeliveryAnalytics("delivery.city_selected", { hasResults: true });
  };

  const listId = "sv2-np-settlement-listbox";
  const showMinHint = !disabled && !value && query.trim().length > 0 && query.trim().length < NP_CITY_MIN_QUERY;
  // Visible whenever we have results and no committed selection (do not require fragile `open` flag).
  const showList = !disabled && !value && items.length > 0;

  return (
    <div className="sv2-field sv2-np-combobox" data-testid="sv2-np-settlement-combobox" data-replace-on-first-input="1">
      <label>{lang === "en" ? "City (Nova Poshta)" : "Місто (Нова Пошта)"} *</label>
      <div className="sv2-np-combobox-input-wrap">
        <input
          ref={inputRef}
          value={query}
          disabled={disabled}
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          onFocus={() => {
            focusedRef.current = true;
            if (value?.label) {
              replaceArmedRef.current = true;
              comboboxFocusSelectAll(inputRef);
            }
          }}
          onBlur={() => {
            focusedRef.current = false;
          }}
          onBeforeInput={(e) => {
            // COMMERCE-LAUNCH-168D1 — never interrupt active IME composition (SwiftKey/Android).
            if ((e.nativeEvent as InputEvent).isComposing) return;
            if (!replaceArmedRef.current || !value?.label || query !== value.label) return;
            const data = (e.nativeEvent as InputEvent).data;
            if (!data) return;
            e.preventDefault();
            applyEdit(data, data);
          }}
          onChange={(e) => {
            applyEdit(e.target.value);
          }}
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
            if (replaceArmedRef.current && value?.label && query === value.label) {
              const hk = handleFirstKeyReplaceSelection(e.key, value.label, query, {
                ctrlKey: e.ctrlKey,
                metaKey: e.metaKey,
                altKey: e.altKey,
              });
              if (hk.handled) {
                e.preventDefault();
                replaceArmedRef.current = false;
                clearSelection();
                setQuery(hk.nextQuery);
                return;
              }
            }
            if (e.key === "Escape") {
              setItems([]);
              setActiveIndex(-1);
              return;
            }
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActiveIndex((i) => Math.min(i + 1, Math.max(items.length - 1, 0)));
              return;
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              setActiveIndex((i) => Math.max(i - 1, 0));
              return;
            }
            if (e.key === "Enter" && activeIndex >= 0 && items[activeIndex]) {
              e.preventDefault();
              pick(items[activeIndex]!);
            }
          }}
          placeholder={lang === "en" ? "Type at least 2 letters…" : "Мінімум 2 літери…"}
          autoComplete="off"
          inputMode="search"
        />
        {value ? (
          <button
            type="button"
            className="sv2-np-combobox-clear"
            aria-label={lang === "en" ? "Clear city" : "Очистити місто"}
            data-testid="sv2-np-settlement-clear"
            onClick={() => {
              clearSelection();
              setQuery("");
              setItems([]);
              replaceArmedRef.current = false;
              searchStartedRef.current = false;
              inputRef.current?.focus();
            }}
          >
            ×
          </button>
        ) : null}
      </div>
      {showMinHint ? (
        <div className="sv2-muted sv2-np-hint" data-testid="sv2-np-settlement-min">
          {npCityHintCopy(copyLang, "min")}
        </div>
      ) : null}
      {loading ? (
        <div className="sv2-muted sv2-np-hint" data-testid="sv2-np-settlement-loading">
          {npCityHintCopy(copyLang, "loading")}
        </div>
      ) : null}
      {!disabled && !value && items.length === 0 && query.trim().length >= NP_CITY_MIN_QUERY && !loading ? (
        <div className="sv2-muted sv2-np-hint" data-testid="sv2-np-settlement-empty">
          {npCityHintCopy(copyLang, "empty")}
        </div>
      ) : null}
      {showList ? (
        <div className="sv2-muted sv2-np-hint" data-testid="sv2-np-settlement-pick-hint">
          {lang === "en"
            ? "Pick a city from the list — typing alone is not enough."
            : "Оберіть місто зі списку — самого тексту недостатньо."}
        </div>
      ) : null}
      {showList ? (
        <ul className="sv2-np-list" role="listbox" id={listId} data-testid="sv2-np-settlement-list">
          {items.map((s, idx) => {
            const hl = highlightParts(s.label, query);
            const meta = [s.region, s.area, s.settlementType].filter(Boolean).join(" · ");
            return (
              <li key={s.settlementRef} role="option" aria-selected={idx === activeIndex}>
                <button
                  type="button"
                  className={`sv2-np-option${idx === activeIndex ? " is-active" : ""}`}
                  onMouseDown={(ev) => ev.preventDefault()}
                  onClick={() => pick(s)}
                >
                  <span className="sv2-np-option-title">
                    {hl ? (
                      <>
                        {hl.before}
                        <mark className="sv2-np-match">{hl.match}</mark>
                        {hl.after}
                      </>
                    ) : (
                      s.label
                    )}
                  </span>
                  {meta ? <span className="sv2-muted sv2-np-option-meta">{meta}</span> : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

export {
  handleFirstKeyReplaceSelection,
  comboboxFocusSelectAll,
  NP_COMBOBOX_DEBOUNCE_MS,
  applyReplaceOnFirstInput,
};
